import React, { useCallback, useContext, useEffect, useState } from "react";

import { Android, Apple, DeviceUnknown } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useParams } from "react-router-dom";

import { DeviceInfo } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/support_pb";

import { DEFAULT_SNACKBAR_DURATION } from "../../../constants";
import { deleteUserToken, listUserDevices } from "api/endpoints/user";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";
import { convertTimestampToDate } from "helpers/time";
import { UserUrlParamTypes } from "types/user-display";

const getDeviceIcon = (userAgent: string) => {
  if (userAgent.includes("iOS")) {
    return <Apple />;
  } else if (userAgent.includes("Android")) {
    return <Android />;
  }
  return <DeviceUnknown />;
};

const UserDevicesTab = () => {
  const { regionCode, userId } = useParams<UserUrlParamTypes>();
  const [isLoading, setIsLoading] = useState(false);

  const [devices, setDevices] = useState<DeviceInfo[]>([]);
  const [disabledDevice, setDisabledDevice] = useState("");

  const [deregisterInfo, setDeregisterInfo] = useState({
    dialogOpen: false,
    deviceName: "",
    deviceId: "",
  });
  const [deregisterSuccessOpen, setDeregisterSuccessOpen] = useState(false);

  const { showError } = useContext(SnackbarContext);

  const updateDeviceList = useCallback(() => {
    setIsLoading(true);
    listUserDevices(
      {
        userId,
      },
      callOptionsHeaders(regionCode, userId)
    )
      .then((resp) => {
        setDevices(resp.devices);
      })
      .catch(showError)
      .finally(() => setIsLoading(false));
  }, [regionCode, showError, userId]);

  const deregisterDevice = (deviceId: string) => {
    setDisabledDevice(deviceId);
    deleteUserToken(
      {
        userId: toUUID(userId),
        tokenId: deviceId,
      },
      callOptionsHeaders(regionCode, userId)
    )
      .then(() => {
        setDisabledDevice("");
        updateDeviceList();
        setDeregisterSuccessOpen(true);
      })
      .catch(showError);
  };

  useEffect(() => {
    updateDeviceList();
  }, [updateDeviceList]);

  const getDevicesList = () => (
    <List>
      {devices.map((device) => (
        <ListItem
          secondaryAction={
            <Button
              color="secondary"
              variant="contained"
              onClick={() => {
                setDeregisterInfo({
                  dialogOpen: true,
                  deviceName: device.name,
                  deviceId: device.id,
                });
              }}
              disabled={device.id === disabledDevice}
            >
              Deregister
            </Button>
          }
          key={device.id}
        >
          <ListItemIcon>{getDeviceIcon(device.agent)}</ListItemIcon>
          <ListItemText
            primary={`${device.name} (${device.agent || "Unknown"}) - ${
              device.deviceId
            }`}
            secondary={
              device.createdAt
                ? DateTime.fromJSDate(
                    convertTimestampToDate(device.createdAt)
                  ).toFormat("MMMM dd yyyy")
                : ""
            }
          />
        </ListItem>
      ))}
    </List>
  );

  if (isLoading) {
    return (
      <Box p={1}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box p={1}>
      {devices.length === 0 ? (
        <Typography>This user has no devices</Typography>
      ) : (
        getDevicesList()
      )}
      <ConfirmDialog
        title="Deregister device"
        open={deregisterInfo.dialogOpen}
        setOpen={(newOpen: boolean) =>
          setDeregisterInfo({ ...deregisterInfo, dialogOpen: newOpen })
        }
        onConfirm={() => {
          deregisterDevice(deregisterInfo.deviceId);
        }}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Deregister"
      >
        Are you sure you want to deregister &quot;
        {deregisterInfo.deviceName}
        &quot;?
      </ConfirmDialog>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={deregisterSuccessOpen}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setDeregisterSuccessOpen(false)}
        data-testid="deregister-device-success"
      >
        <Alert severity="success" variant="filled">
          Successfully deregistered device
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default UserDevicesTab;
